<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeMount, ref } from 'vue';

  import type { Partner, Task } from '@stuut/api/models';
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Button } from '@ui/components/button';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import { useTaskCollectionStore, useTaskStore } from '@/store';

  const taskStore = useTaskStore();
  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);

  const partner = ref<Partner>();
  const isReverting = ref(false);
  const showConfirmDialog = ref(false);

  const info = computed(() => {
    const d = (currentTask.value?.acknowledgeTaskData?.data as any) || undefined;

    return (
      d?.pause_state_update_info ||
      (d?.old_state || d?.new_state
        ? { new_state: d?.new_state, old_state: d?.old_state }
        : undefined)
    );
  });

  const oldState = computed(() => info.value?.old_state);
  const newState = computed(() => info.value?.new_state);

  const canRevert = computed(
    () =>
      Boolean(currentTask.value?.isOpen || currentTask.value?.isCompleted) &&
      Boolean(partner.value?.id) &&
      Boolean(oldState.value?.id) &&
      // Only while the partner is still on the new state — once reverted (or changed
      // elsewhere) the partner is off it, so disable to prevent re-firing the revert.
      partner.value?.dunning_state_id === newState.value?.id &&
      !isReverting.value,
  );

  const openConfirmDialog = () => {
    showConfirmDialog.value = true;
  };

  const closeConfirmDialog = () => {
    showConfirmDialog.value = false;
  };
  const revertAndComplete = async () => {
    if (!canRevert.value) return;
    isReverting.value = true;
    try {
      await stuut.partners.update(
        (partner.value as Partner).id,
        { data: { dunning_state: oldState.value!.id } },
        partner.value as Partner,
      );
      // Only complete the task if it's still open — the revert can also run on an already-completed task.
      if (currentTask.value?.isOpen) {
        await stuut.tasks.handleTaskAction(currentTask.value as Task, 'complete', {
          close_reason: 'Reverted to old state',
        });
      }
      toast.success('Reverted dunning state.');
      taskCollectionStore.afterCompleteTask();
      taskCollectionStore.refreshAllTaskTables();
      closeConfirmDialog();
    } catch (err: any) {
      toast.error(err?.clientMessage || 'An error occurred');
    } finally {
      isReverting.value = false;
    }
  };

  onBeforeMount(async () => {
    const partnerId = currentTask.value?.partner_id;
    if (partnerId) {
      partner.value = (await taskStore.peekFindPartner(partnerId)) || undefined;
    }
  });
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-2">
        <strong class="text-muted-foreground">Dunning state updated</strong>
      </div>
      <Button
        size="inline"
        variant="link"
        class="text-xs text-blue font-bold"
        :disabled="!canRevert"
        :loading="isReverting"
        @click="openConfirmDialog"
      >
        Revert to previous state
      </Button>
    </div>

    <div
      class="grid grid-cols-[1fr_auto_1fr] items-start gap-x-3 p-3 border rounded bg-secondary/40"
    >
      <!-- Row 1: Titles -->
      <div class="text-2xs text-muted-foreground row-start-1">Previous state</div>
      <i class="far fa-arrow-right text-muted-foreground row-span-3 self-center"></i>
      <div class="text-2xs text-muted-foreground row-start-1 col-start-3">New state</div>

      <!-- Row 2: Names -->
      <div class="font-semibold row-start-2">
        {{ oldState?.name }}
      </div>
      <div class="font-semibold row-start-2 col-start-3">
        {{ newState?.name }}
      </div>

      <!-- Row 3: Descriptions -->
      <div v-if="oldState?.description" class="text-2xs text-muted-foreground row-start-3">
        {{ oldState?.description }}
      </div>
      <div
        v-if="newState?.description"
        class="text-2xs text-muted-foreground row-start-3 col-start-3"
      >
        {{ newState?.description }}
      </div>
    </div>
  </div>

  <AlertDialog :open="showConfirmDialog" @update:open="(status) => (showConfirmDialog = status)">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Confirm</AlertDialogTitle>
        <AlertDialogDescription class="space-y-2.5">
          <p>
            Revert customer state to <strong>{{ oldState?.name }}</strong>
            <template v-if="currentTask?.isOpen"> and close this task</template>?
          </p>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel :disabled="isReverting" @click="closeConfirmDialog">
          Cancel
        </AlertDialogCancel>

        <AlertDialogAction :disabled="!canRevert" :loading="isReverting" @click="revertAndComplete">
          Confirm
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped></style>
